import React, { useState, useEffect, useMemo } from 'react';
import CustomDialog from 'components/CustomDialog/customDialog';
import { useDispatch, useSelector } from 'react-redux';
import { getDialogState } from 'store/selectors/dialogSelectors';
import { setDialog } from 'store/actions/dialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { SearchTemplatesContentCnt } from 'components/AllDialogs/templates/templatesWithFoldersHierarchy/styles';
import DIALOGS from 'utils/dialogIds';
import FilesAndFoldersList from 'components/AllDialogs/templates/templatesWithFoldersHierarchy/FilesAndFoldersList';
import SearchInput from 'components/AllDialogs/templates/templatesWithFoldersHierarchy/templatesWithFoldersHierarchy';
import EmptyState from 'components/AttachChecklistModal/EmptyFilter';
import {
  Typography,
  Box,
  CircularProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import CustomDialogHeader from 'components/CustomDialog/DialogHeader/dialogHeader.cmp';
import CustomButton from 'components/Button/CustomButton';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import { getChecklistListing, getTemplateFolders } from 'api/checklistV2Api';
import { cloneDeep, filter, flatMap, groupBy } from 'lodash';
import useTemplateBuilder from 'utils/CustomHooks/templateBuilder/useTemplateBuilder';
import ArrowRightIcon from '@mui/icons-material/ArrowForwardIos';
import TemplateFolderIcon from 'components/Icons/templateFolderIcon';
import CTypography from 'components/CTypography';
import { BreadCrumbsCnt } from 'components/AttachChecklistModal/TemplateList/style';
import { styled } from '@mui/material/styles';

// Styled components for the missing elements
const DottedContainer = styled(Box)(() => ({
  padding: '8px',
  maxHeight: '200px',
  overflowY: 'auto',
}));

const DottedText = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#212121',
  fontWeight: 500,
}));

function SelectTemplateDialog() {
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const { navigateWithWorkspaceUrl } = useWorkspaceHook();
  const { organizeChecklistIntoHierarchy } = useTemplateBuilder();

  const dialogState = useSelector(getDialogState);
  const open = dialogState?.[DIALOGS.SELECT_TEMPLATE]?.open;
  const modalData = dialogState?.[DIALOGS.SELECT_TEMPLATE]?.data;
  const dispatch = useDispatch();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [folders, setFolders] = useState<any>({
    isLoading: true,
    data: null,
  });
  const [templates, setTemplates] = useState<any>({
    isLoading: true,
    data: null,
  });
  const [selectedFolderTemplates, setSelectedFolderTemplates] =
    useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get Folders and Templates Data
  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = () => {
    getFolders();
    getTemplates();
  };

  const getFolders = async () => {
    const foldersList = await getTemplateFolders({
      task: true, // In order to get non adhoc folders
    });
    const foldersWithChecklists = foldersList?.data?.filter(
      (item) => item.itemsCount != 0,
    );
    setFolders({ ...folders, data: foldersWithChecklists });
  };

  const getTemplates = async () => {
    const response = await getChecklistListing({
      detailed: true,
      archived: false,
      task: true, // In order to get non adhoc templates
      ...(modalData?.filterByApprovals && {
        withoutApprovals: true,
      }),
    });

    const updatedResponse = response?.data?.map((item) => {
      const transformed = {
        ...item,
        TaskChecklistItems: item?.ChecklistItems,
        TaskChecklistSections: item?.ChecklistSections,
      };

      return {
        ...item,
        ChecklistItems: organizeChecklistIntoHierarchy(transformed),
      };
    });

    const groupedTemplates = groupBy(
      updatedResponse,
      'HotelChecklist.FolderId',
    );
    setTemplates({ ...templates, data: groupedTemplates });
  };

  useEffect(() => {
    const isDataReady =
      folders?.data &&
      templates?.data &&
      templates?.isLoading &&
      folders?.isLoading;

    if (isDataReady) {
      const foldersList = cloneDeep(folders.data);
      const foldersWithChecklists = foldersList.map((folder) => {
        folder.checklists = templates?.data?.[folder?.id];
        return folder;
      });

      setFolders({
        isLoading: false,
        data: foldersWithChecklists,
      });

      setTemplates({
        isLoading: false,
        data: templates?.data?.['null'] || [],
      });
    }
  }, [folders, templates]);

  const folderClickHandler = (folder) => {
    const selectedFolder = folders?.data?.find(
      (item) => item?.id === folder?.id,
    );

    setSelectedFolderTemplates(selectedFolder);
    setSearchTerm('');
  };

  const backToChecklistHome = () => {
    setSelectedFolderTemplates(null);
    setSearchTerm('');
  };

  const closeAction = () => {
    dispatch(
      setDialog({
        open: false,
        dialogId: DIALOGS.SELECT_TEMPLATE,
      }),
    );
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleStartSubmission = () => {
    if (selectedTemplateId) {
      navigateWithWorkspaceUrl(
        `/in-progress-checklist/${selectedTemplateId}?prevUrl=${`/dashboard`}`,
      );
      closeAction();
    }
  };

  const handleCancel = () => {
    if (modalData?.onCancel) {
      modalData.onCancel();
    }
    closeAction();
  };

  // Root Templates Filtering
  const filteredTemplates = useMemo(() => {
    if (templates?.isLoading) {
      return [];
    }

    if (searchTerm) {
      let templatesList =
        templates?.data?.filter((checklist) =>
          checklist?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
        ) || [];

      const filteredChecklists =
        flatMap(folders?.data, (folder) =>
          filter(folder?.checklists, (item) =>
            item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
          ),
        ) || [];

      templatesList = [...templatesList, ...filteredChecklists];

      return templatesList?.length > 0 ? templatesList : templates?.data;
    }

    const filtered = templates?.data?.filter((checklist) =>
      checklist?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
    );

    return filtered?.length ? filtered : templates?.data;
  }, [templates, searchTerm, selectedFolderTemplates]);

  // Folders Filtering
  const filteredFolders = useMemo(() => {
    if (folders?.isLoading) {
      return [];
    }

    const filtered = folders?.data?.filter((folder) =>
      folder?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
    );

    return filtered?.length
      ? filtered
      : folders?.data?.filter((folder) => !folder?.ParentId);
  }, [folders, searchTerm, selectedFolderTemplates]);

  // Templates Inside Folders Filtering
  const filteredFolderTemplates = useMemo(() => {
    if (!selectedFolderTemplates) {
      return null;
    }

    const filtered = selectedFolderTemplates?.checklists?.filter((checklist) =>
      checklist?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
    );

    return filtered?.length
      ? {
          ...selectedFolderTemplates,
          checklists: filtered,
        }
      : selectedFolderTemplates;
  }, [selectedFolderTemplates, searchTerm]);

  const foldersData = useMemo(() => {
    if (searchTerm && !selectedFolderTemplates) return filteredFolders;

    return selectedFolderTemplates
      ? filteredFolders?.filter(
          (folder) => folder?.ParentId == selectedFolderTemplates?.id,
        )
      : filteredFolders?.filter((folder) => !folder?.ParentId);
  }, [selectedFolderTemplates, filteredFolders, searchTerm]);

  const templatesData = useMemo(() => {
    return selectedFolderTemplates
      ? filteredTemplates?.filter(
          (temp) => temp?.parentId == selectedFolderTemplates?.id,
        )
      : filteredTemplates?.filter((temp) => !temp?.parentId);
  }, [selectedFolderTemplates, filteredTemplates, searchTerm]);

  const loading = folders?.isLoading || templates?.isLoading;
  const haveData = folders?.data?.length > 0 || templates?.data?.length > 0;

  return (
    <CustomDialog
      hideActions={true}
      header={
        <CustomDialogHeader onClose={closeAction}>
          <Typography fontSize={'20px'} fontWeight={700}>
            Choose a Template
          </Typography>
        </CustomDialogHeader>
      }
      dialogProps={{
        open: open,
        fullWidth: true,
        fullScreen: isMobileDevice,
        onClose: closeAction,
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: '650px',
          },
        },
      }}
    >
      <SearchTemplatesContentCnt>
        <SearchInput onSearchChange={setSearchTerm} />

        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !haveData && <EmptyState />}

        {!loading && haveData && (
          <>
            {selectedFolderTemplates && (
              <Box sx={{ display: 'flex', padding: '16px 0' }}>
                <BreadCrumbsCnt
                  selectedFolder={selectedFolderTemplates ?? true}
                >
                  <CTypography
                    variant={'h1'}
                    className={'heading'}
                    onClick={backToChecklistHome}
                    sx={{ cursor: 'pointer' }}
                  >
                    Folders
                  </CTypography>

                  {selectedFolderTemplates?.path?.length >= 1 && (
                    <>
                      <ArrowRightIcon
                        style={{
                          color: '#757575',
                          margin: '0px 6px',
                          fontSize: 11,
                        }}
                      />
                      <Tooltip
                        arrow
                        PopperProps={{
                          sx: {
                            zIndex: 1300,
                            '& .MuiTooltip-tooltip': {
                              backgroundColor: 'white !important',
                              boxShadow:
                                'rgba(0, 0, 0, 0.2) 0px 10px 15px 3px, rgba(0, 0, 0, 0.2) 0px 4px 6px -2px',
                              minWidth: 100,
                              maxHeight: 300,
                              overflowY: 'auto',
                              borderRadius: '15px',
                            },
                          },
                        }}
                        title={
                          <DottedContainer>
                            {selectedFolderTemplates?.path?.map((item) => {
                              return (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap="10px"
                                  key={item?.id}
                                  sx={{
                                    cursor: 'pointer',
                                    marginBottom: '10px',
                                  }}
                                  onClick={() => {
                                    folderClickHandler(item);
                                  }}
                                >
                                  <TemplateFolderIcon
                                    primaryColor={item?.meta?.primaryColor}
                                    secondaryColor={item?.meta?.secondaryColor}
                                  />
                                  <DottedText>{item?.title}</DottedText>
                                </Stack>
                              );
                            })}
                          </DottedContainer>
                        }
                      >
                        <div style={{ cursor: 'pointer' }}>...</div>
                      </Tooltip>
                    </>
                  )}
                </BreadCrumbsCnt>
                <BreadCrumbsCnt
                  selectedFolder={selectedFolderTemplates ?? true}
                >
                  <ArrowRightIcon
                    style={{
                      color: '#757575',
                      margin: '0px 6px',
                      fontSize: 11,
                    }}
                  />
                  <div className={'folderNameCnt'}>
                    <TemplateFolderIcon
                      primaryColor={selectedFolderTemplates?.meta?.primaryColor}
                      secondaryColor={
                        selectedFolderTemplates?.meta?.secondaryColor
                      }
                    />
                    <CTypography variant={'h1'} className={'folderName'}>
                      {selectedFolderTemplates?.title}
                    </CTypography>
                  </div>
                </BreadCrumbsCnt>
              </Box>
            )}

            <FilesAndFoldersList
              onTemplateSelect={handleTemplateSelect}
              selectedTemplateId={selectedTemplateId}
              folders={foldersData}
              templates={
                selectedFolderTemplates
                  ? filteredFolderTemplates?.checklists
                  : templatesData
              }
              onFolderClick={folderClickHandler}
              searchTerm={searchTerm}
            />
          </>
        )}
      </SearchTemplatesContentCnt>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          p: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
        }}
      >
        <CustomButton variant="text" buttonType="gray" onClick={handleCancel}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={handleStartSubmission}
          disabled={!selectedTemplateId}
        >
          Start
        </CustomButton>
      </Box>
    </CustomDialog>
  );
}

export default SelectTemplateDialog;
